// Zwei Darstellungen desselben Reiters „Allgemein" der Kontaktakte.
//
// „klassisch" ist die gewachsene Gliederung: Überschrift, goldene Trennlinie
// darunter, Klapp-Chevron links. „karten" zeigt dieselben Abschnitte als weiße
// Karten mit Schatten — dieselbe Optik wie die Kontakt-Anlage, damit wer viel
// anlegt und danach in der Akte weiterarbeitet nicht zwischen zwei Welten
// wechselt.
//
// Umgeschaltet wird über einen Knopf im Aktenkopf; die Wahl gilt
// kontaktübergreifend, wie schon der zugeklappte Kopf und die Speicherleiste.
// Die Voreinstellung bleibt bewusst die klassische Ansicht: die neue Optik holt
// sich, wer sie will — sie wird niemandem untergeschoben.
//
// Wichtig: Die Ansicht ist reine Darstellung. Sie fasst kein Feld an, ändert
// keinen Formularzustand und löst kein Speichern aus. Genau daran hängt, dass
// die Speicherleiste beim Umschalten ruhig bleibt.

import * as React from "react";

export type AkteAnsicht = "klassisch" | "karten";

/** Gemerkte Ansicht. Schlüssel wie bei der Speicherleiste (`kmvs.contactDetails.*`). */
export const AKTE_ANSICHT_KEY = "kmvs.contactDetails.ansicht";

/**
 * Präfix des gemerkten Klappzustands je Karte; angehängt wird der
 * Abschnittstitel. Eine Titeländerung setzt den Zustand dieser einen Karte
 * zurück — sie steht dann wieder offen, was harmlos ist.
 */
export const AKTE_KARTE_KEY_PREFIX = "kmvs.contactDetails.karte.";

/**
 * Liest den gemerkten Wert. Alles Unbekannte — nichts gemerkt, ein Altwert aus
 * einer früheren Fassung, ein von Hand verstellter Eintrag — fällt auf die
 * klassische Ansicht zurück, statt eine leere Akte zu riskieren.
 */
export function ansichtAusSpeicher(roh: string | null | undefined): AkteAnsicht {
  return roh === "karten" ? "karten" : "klassisch";
}

/** Die jeweils andere Ansicht — Vorlage für den Umschalter. */
export function andereAnsicht(a: AkteAnsicht): AkteAnsicht {
  return a === "karten" ? "klassisch" : "karten";
}

/**
 * Beschriftung des Umschalters. Sie benennt das ZIEL, nicht den Zustand: Der
 * Knopf sagt, was ein Klick bringt.
 */
export function umschalterTitel(ziel: AkteAnsicht): string {
  return ziel === "karten" ? "Kartenansicht anzeigen" : "Klassische Ansicht anzeigen";
}

/** Merkt die Ansicht. Ohne localStorage (Privatmodus) bleibt sie für die Sitzung. */
export function merkeAnsicht(a: AkteAnsicht): void {
  try {
    window.localStorage.setItem(AKTE_ANSICHT_KEY, a);
  } catch {
    /* localStorage nicht verfügbar — die Wahl gilt nur bis zum Neuladen */
  }
}

/** Zuletzt gemerkte Ansicht, oder die klassische. */
export function gemerkteAnsicht(): AkteAnsicht {
  try {
    return ansichtAusSpeicher(window.localStorage.getItem(AKTE_ANSICHT_KEY));
  } catch {
    return "klassisch";
  }
}

/**
 * Die Abschnittskomponente der Akte liest die Ansicht hier heraus. Über einen
 * Kontext und nicht über eine Prop, weil sonst alle vier Layout-Funktionen und
 * jeder der Abschnitts-Aufrufe angefasst werden müssten — für eine Angabe, die
 * für die ganze Akte gleich ist.
 */
export const AkteAnsichtCtx = React.createContext<AkteAnsicht>("klassisch");

export function useAkteAnsicht(): AkteAnsicht {
  return React.useContext(AkteAnsichtCtx);
}
